/**
 * Structure Host Tool
 *
 * Hosts the shared `LineTool` subtool (with a `StructureKindAdapter`) for the
 * structure workflow, mirroring `RoomHostTool`. One instance per structure
 * type: the Magic Beam and Magic Column tools each construct this with their
 * `StructureKind`, which the adapter applies at commit time.
 */

import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { StructureKind } from 'shared-types';
import { SubToolHosting } from '../../SubToolHosting';
import { LineTool } from './LineTool';
import { StructureKindAdapter } from './line-tool/StructureKindAdapter';
import { levelZRange } from '@pointorama/potree-shared';
import type { ResolvedLevelBounds } from '@pointorama/potree-shared';
import { DetectedFloorPlane } from '../../../utils/floorDetection';
import { FloorIsolationStrategy, IsolationSpec, boundsFromPlanes } from '../../../utils/FloorIsolation';

/** Floor context handed to a resolver to build a clip strategy at runtime. */
export interface StructureIsolationContext {
  floorGroupId: string;
  floor: DetectedFloorPlane;
  ceiling: DetectedFloorPlane | null;
  /** Level bounds resolved against all known floor groups. */
  bounds: ResolvedLevelBounds;
}

/** Builds the clip strategy a structure tool should use for the active floor. */
export type StructureIsolationStrategyResolver = (ctx: StructureIsolationContext) => FloorIsolationStrategy;

/**
 * Default structure isolation: clip base → next floor ('toNextFloor'), so the
 * structural slab above the true ceiling is included and beams hanging under
 * it stay visible, while everything outside this storey is hidden.
 */
export const fullFloorSlabIsolationStrategy: StructureIsolationStrategyResolver = ({ floorGroupId, bounds }) => {
  const { minZ, maxZ } = levelZRange(bounds, 'toNextFloor');
  return { kind: 'roomBounds', floorGroupId, floorHeight: minZ, ceilingHeight: maxZ };
};

/**
 * Scale every structure's vertex spheres (1 = full size for the structure
 * tools, 0.5 = shrunken elsewhere) and remember the value on the scene so
 * sphere rebuilds outside the tool re-apply the same size. Shared by the
 * magic (this file) and manual structure hosts.
 */
export function setStructureSphereScale(viewer: Viewer, scale: number): void {
  viewer.scene.structureSphereScale = scale;
  for (const structure of viewer.scene.structures) {
    for (const sphere of structure.spheres) sphere.setScaleFactor(scale);
  }
}

export class StructureHostTool extends SubToolHosting(FloorplanToolBase) {
  readonly name: string;

  /** Typed reference to the hosted LineTool for LineTool-specific calls. */
  private readonly lineTool: LineTool;

  /**
   * Optional per-instance clip strategy. When omitted the tool falls back to
   * the FloorplanToolBase default (2.1m fixed height), so beam/column can
   * diverge without redeclaring the default.
   */
  private readonly isolationStrategy?: StructureIsolationStrategyResolver;

  constructor(viewer: Viewer, persona: IPersona, kind: StructureKind, isolationStrategy?: StructureIsolationStrategyResolver) {
    super(viewer, persona);

    this.name = kind === StructureKind.BEAM ? 'beam_draw' : 'column_draw';
    this.isolationStrategy = isolationStrategy;

    const adapter = new StructureKindAdapter(kind);
    this.lineTool = new LineTool(viewer, persona, undefined, adapter);
    const ctx = adapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(this.lineTool, adapter, ctx);
  }

  // Lifecycle is otherwise owned by SubToolHosting; these overrides only add
  // the vertex-sphere sizing (full size in the tool, half size outside).

  protected onEnable(): void {
    super.onEnable();
    setStructureSphereScale(this.viewer, 1);
  }

  protected onDisable(): void {
    setStructureSphereScale(this.viewer, 0.5);
    super.onDisable();
  }

  protected onFloorChanged(): void {
    this.pushBasePlane();
  }

  /**
   * Full-floor slab for EVERY view (no X-Ray exception): beams/columns span
   * floor→ceiling, so the same slab is wanted in both 3D and X-Ray. Falls back
   * to the base default when no custom strategy was supplied.
   */
  protected getIsolationSpec(): IsolationSpec {
    const floorGroupId = this.currentFloorGroupIdentifier;
    if (!this.isolationStrategy || !floorGroupId || !this.currentFloorFloorPlane) {
      return super.getIsolationSpec();
    }
    const bounds = this.viewer.floorIsolationManager.resolveLevelBounds(floorGroupId) ?? boundsFromPlanes(floorGroupId, this.currentFloorFloorPlane, this.currentFloorCeilingPlane);
    const strategy = this.isolationStrategy({
      floorGroupId,
      floor: this.currentFloorFloorPlane,
      ceiling: this.currentFloorCeilingPlane,
      bounds,
    });
    return { base: strategy };
  }
}
